package br.com.heyagenda.ui.theme

import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.graphics.Color

@Immutable
data class AuraColors(
    val isDark: Boolean,
    val background: Color,
    val cardSurface: Color,
    val cardSurfaceHover: Color,
    val modalSurface: Color,
    val borderSubtle: Color,
    val borderHover: Color,
    val pillBg: Color,
    val pillBorder: Color,
    val primaryButtonBg: Color,
    val primaryButtonText: Color,
    val secondaryButtonBg: Color,
    val secondaryButtonBorder: Color,
    val textPrimary: Color,
    val textSecondary: Color,
    val textMuted: Color,
    val emerald: Color,
    val onEmerald: Color,
    val violet: Color,
    val amber: Color,
    val ruby: Color
)

val DarkAuraColors = AuraColors(
    isDark = true,
    background = Color(0xFF050507),
    cardSurface = Color(0x09FFFFFF),
    cardSurfaceHover = Color(0x0FFFFFFF),
    modalSurface = Color(0xFF050507),
    borderSubtle = Color(0x14FFFFFF),
    borderHover = Color(0x24FFFFFF),
    pillBg = Color(0x0DFFFFFF),
    pillBorder = Color(0x1AFFFFFF),
    primaryButtonBg = Color(0xFFFFFFFF),
    primaryButtonText = Color(0xFF000000),
    secondaryButtonBg = Color(0x14FFFFFF),
    secondaryButtonBorder = Color(0x1FFFFFFF),
    textPrimary = Color(0xFFFFFFFF),
    textSecondary = Color(0x99FFFFFF),
    textMuted = Color(0x73FFFFFF),
    emerald = Color(0xFFC3F03C),
    onEmerald = Color(0xFF032211),
    violet = Color(0xFF8B5CF6),
    amber = Color(0xFFF59E0B),
    ruby = Color(0xFFEF4444)
)

val LightAuraColors = AuraColors(
    isDark = false,
    background = Color(0xFFF6F8FA),
    cardSurface = Color(0xFFFFFFFF),
    cardSurfaceHover = Color(0xFFF1F5F9),
    modalSurface = Color(0xFFFFFFFF),
    borderSubtle = Color(0xFFE2E8F0),
    borderHover = Color(0xFFCBD5E1),
    pillBg = Color(0xFFF1F5F9),
    pillBorder = Color(0xFFE2E8F0),
    primaryButtonBg = Color(0xFF0F172A),
    primaryButtonText = Color(0xFFFFFFFF),
    secondaryButtonBg = Color(0xFFF1F5F9),
    secondaryButtonBorder = Color(0xFFE2E8F0),
    textPrimary = Color(0xFF0F172A),
    textSecondary = Color(0xFF475569),
    textMuted = Color(0xFF94A3B8),
    emerald = Color(0xFFC3F03C),
    onEmerald = Color(0xFF032211),
    violet = Color(0xFF7C3AED),
    amber = Color(0xFFD97706),
    ruby = Color(0xFFDC2626)
)

val LocalAuraColors = staticCompositionLocalOf { DarkAuraColors }

object AuraTheme {
    val colors: AuraColors
        @Composable
        get() = LocalAuraColors.current

    val isDark: Boolean
        @Composable
        get() = LocalAuraColors.current.isDark
}

// Backward-compatible dynamic accessors for all composables
val AuraBackground: Color @Composable get() = AuraTheme.colors.background
val AuraCardSurface: Color @Composable get() = AuraTheme.colors.cardSurface
val AuraCardSurfaceHover: Color @Composable get() = AuraTheme.colors.cardSurfaceHover
val AuraModalSurface: Color @Composable get() = AuraTheme.colors.modalSurface
val AuraBorderSubtle: Color @Composable get() = AuraTheme.colors.borderSubtle
val AuraBorderHover: Color @Composable get() = AuraTheme.colors.borderHover

val AuraPillBg: Color @Composable get() = AuraTheme.colors.pillBg
val AuraPillBorder: Color @Composable get() = AuraTheme.colors.pillBorder

val AuraPrimaryButtonBg: Color @Composable get() = AuraTheme.colors.primaryButtonBg
val AuraPrimaryButtonText: Color @Composable get() = AuraTheme.colors.primaryButtonText

val AuraSecondaryButtonBg: Color @Composable get() = AuraTheme.colors.secondaryButtonBg
val AuraSecondaryButtonBorder: Color @Composable get() = AuraTheme.colors.secondaryButtonBorder

val AuraTextPrimary: Color @Composable get() = AuraTheme.colors.textPrimary
val AuraTextSecondary: Color @Composable get() = AuraTheme.colors.textSecondary
val AuraTextMuted: Color @Composable get() = AuraTheme.colors.textMuted

// Accents
val AuraEmerald: Color @Composable get() = AuraTheme.colors.emerald
val AuraOnEmerald: Color @Composable get() = AuraTheme.colors.onEmerald
val AuraViolet: Color @Composable get() = AuraTheme.colors.violet
val AuraAmber: Color @Composable get() = AuraTheme.colors.amber
val AuraRuby: Color @Composable get() = AuraTheme.colors.ruby
